import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import { asArray } from "@/lib/asArray";
import { entryRateText } from "@/lib/entryRateText";   // RATE HISTORY VOID: "Rate not set - test rate removed"
import React, { useEffect, useRef, useState } from "react";
import useEffectEvent from "@/lib/useEffectEvent";   // Step 7c · E3: the effect runs on its own deps only (see the file)
import DateInput from "@/components/DateInput";
import { fmtDate as fmtDateDt } from "@/lib/dt";
import { useEngagement } from "@/contexts/EngagementContext";
import { useAuth } from "@/contexts/AuthContext";
import api, { API_URL, toErrMsg } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Download, TrendingDown, TrendingUp, Clock, Pencil, Info, Plus, Trash2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { askConfirm } from "@/lib/appDialog";   // Batch 15 · N4 (F-118)
import { useI18n } from "@/contexts/I18nContext";
import TableSaveBar, { RowSaveError } from "@/components/TableSaveBar";   // Batch 28 · Follow-up 1 · F2 (b)
import { useTableDraft } from "@/lib/useDrafts";
import { fmtHours, fill } from "@/components/dashboard/fdTheme";   // STEP 2 · A2: the dashboard's hours / minutes rule
import { fmtNumber } from "@/components/Amount";   // STEP 3 · rule 2 · B-2e: the one amount formatter

const OTHER_COST_CATEGORIES = ["Travel", "Subscription", "Disbursement", "Other"];
const todayISO = () => new Date().toISOString().slice(0, 10);

const fmt = (n, currency = "") => {
  if (n === null || n === undefined) return "—";
  const s = fmtNumber(Number(n));
  return currency ? `${currency} ${s}` : s;
};
const hrs = (secs) => (Number(secs || 0) / 3600).toFixed(2);

// Stage 2 Dashboard · 2.5: the rate APPLIED to a person / grade line — the rate stored on its entries (the rate in force when
// each was logged), one figure or several ("100.00 / 150.00"); a line with no rated entry is "Rate not set" (never 0.00).
const isUnrated = (line) => !line || line.rate_source === "unrated" || line.hourly_rate == null;
const rateText = (line, t, currency) => {
  if (isUnrated(line)) return t("dsBilling.rateNotSet");
  const rates = Array.isArray(line.rates_applied) && line.rates_applied.length ? line.rates_applied : [line.hourly_rate];
  if (rates.length === 1) return fmt(rates[0], line.currency || currency);
  const s = rates.map((r) => fmt(r)).join(" / ");
  const cur = line.currency || currency;
  return cur ? `${cur} ${s}` : s;
};
const costText = (line, t, currency) => (line.cost == null ? t("dsBilling.rateNotSet") : fmt(line.cost, currency));

// Deterministic grade → HSL colour. Hash the grade name to a hue so the same
// grade always renders in the same colour across tabs (by-user + by-grade)
// and across sessions. Gives every firm's grade list a consistent visual key
// without needing a colour picker in the rate card.
function gradeColor(grade) {
  const g = String(grade || "").trim().toLowerCase();
  if (!g || g === "(no grade)") {
    return { bg: "#f1f5f9", border: "#cbd5e1", text: "#475569", swatch: "#94a3b8" };
  }
  let h = 0;
  for (let i = 0; i < g.length; i++) h = (h * 31 + g.charCodeAt(i)) >>> 0;
  const hue = h % 360;
  return {
    bg: `hsl(${hue} 90% 96%)`,
    border: `hsl(${hue} 60% 78%)`,
    text: `hsl(${hue} 55% 32%)`,
    swatch: `hsl(${hue} 62% 52%)`,
  };
}

// Grade badge — used everywhere in the Billing tab. Colour is derived from
// the grade name; palette works on light + dark theme (dark uses text colour
// on the swatch dot as accent so the label stays legible).
function GradeBadge({ grade, testid }) {
  const c = gradeColor(grade);
  return (
    <span
      className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-medium"
      style={{ backgroundColor: c.bg, color: c.text, border: `1px solid ${c.border}` }}
      data-testid={testid}
    >
      <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: c.swatch }} />
      {grade || "—"}
    </span>
  );
}

export default function Billing() {
  const colW3 = useResizableColumns({ storageKey: "billing-member-budgets", defaults: [240, 110, 130, 160], mins: [130, 72, 90, 100] });   // Step 11d · add-on
  const colW2 = useResizableColumns({ storageKey: "billing-grade-rates", defaults: [110, 130, 130, 130], mins: [72, 90, 90, 90] });   // Step 11d · add-on
  const colW = useResizableColumns({ storageKey: "billing-users", defaults: [240, 110, 130, 130, 130, 130, 130], mins: [130, 72, 90, 90, 90, 90, 90] });   // Step 11d · add-on
  const { user } = useAuth();
  const { current } = useEngagement();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [editFee, setEditFee] = useState(0);
  const [editHours, setEditHours] = useState(0);
  const [savingEdit, setSavingEdit] = useState(false);
  const [grades, setGrades] = useState([]);
  const [breakdownUser, setBreakdownUser] = useState(null); // {user_id, user_name}
  const openedFromUrl = useRef(false);   // Stage 2 · 2.3: the ?user= deep link opens the breakdown once

  const isPartner = user?.role === "Partner" || user?.role === "Admin";
  const { lang, t } = useI18n();
  // Batch 28 · Follow-up 1 · F2 (b): each member's budgeted hours is a row draft — one Save / Cancel bar under the By User table;
  // Save sends only the changed members (the existing member-budget PATCH, one per member); a failed row stays highlighted.
  // The fee / budget dialog and the other-cost dialog already save on their own Save button; the grade picker (a firm-wide
  // assignment), the breakdown and the exports stay immediate.
  const td = useTableDraft({
    rows: data ? asArray(data.by_user).filter((u) => u.user_id) : [], getId: (u) => u.user_id,
    saveRow: (uid, patch) => api.patch(`/billing/${current.id}/member-budget`, { user_id: uid, budgeted_hours: Number(patch.budgeted_hours) || 0 }, { _silent: true }),
    reload: async () => { const r = await api.get(`/billing/${current.id}/overview`); setData(r.data); },
  });

  const refresh = async () => {
    setLoading(true); setErr("");
    try {
      const [ov, gr] = await Promise.all([
        api.get(`/billing/${current.id}/overview`),
        api.get(`/billing/grades`),
      ]);
      setData(ov.data);
      setGrades(asArray(gr.data).map((g) => g.grade).filter(Boolean));
      // Stage 2 · 2.3 (dashboard Staff time row): /billing?user=<id> opens that person's hours breakdown once the overview is in
      try {
        const uid = new URLSearchParams(window.location.search).get("user");
        const u = uid ? asArray(ov.data?.by_user).find((x) => x.user_id === uid) : null;
        if (u && !openedFromUrl.current) { openedFromUrl.current = true; setBreakdownUser({ user_id: u.user_id, user_name: u.user_name }); }
      } catch { /* ignore */ }
    } catch (e) {
      setErr(toErrMsg(e, "Failed to load"));
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!current || !isPartner) return;
    refresh();
    // eslint-disable-next-line
  }, [current?.id, isPartner]);

  const exportFile = async (fmt) => {
    try {
      const token = localStorage.getItem("af_token");
      const url = `${API_URL}/billing/${current.id}/export?fmt=${fmt}`;
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) { toast.error("Export failed"); return; }
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `billing_${current.id.slice(0, 8)}.${fmt}`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error("Export failed"); }
  };

  const openEdit = () => {
    setEditFee(data?.engagement?.contracted_fee || 0);
    setEditHours(data?.engagement?.budgeted_hours || 0);
    setEditOpen(true);
  };
  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      await api.patch(`/engagements/${current.id}`, {
        contracted_fee: Number(editFee), budgeted_hours: Number(editHours),
      });
      toast.success("Fee / budget updated — logged to Audit Trail");
      setEditOpen(false);
      const r = await api.get(`/billing/${current.id}/overview`);
      setData(r.data);
    } catch (e) { toast.error(toErrMsg(e, "Update failed")); }
    finally { setSavingEdit(false); }
  };

  if (!isPartner) {
    return (
      <Card className="border-red-200 bg-red-50/60 dark:border-red-900 dark:bg-red-950/40">
        <CardContent className="p-8 text-center text-sm text-red-800 dark:text-red-200">
          Access denied. The Billing / Overrun Status tab is restricted to Partner-level users.
        </CardContent>
      </Card>
    );
  }
  if (!current) return <div className="text-sm text-muted-foreground">Select an engagement to view billing.</div>;
  if (loading) return <div className="text-sm text-muted-foreground">Loading…</div>;
  if (err) return <div className="text-sm text-red-600">{err}</div>;
  if (!data) return null;

  const { engagement, totals, by_user, by_grade } = data;
  const currency = String(engagement.currency ?? "").trim();   // PR 45 review · D2: exactly as stored, never a guessed "AED"
  // Stage 2 Dashboard · 2.5 / A-18: no budget → "No budget set" (grey), no over-run flag, no "/ 0 hrs", no 0 %
  const budgetSet = totals.budget_set ?? (Number(engagement.budgeted_hours || 0) > 0);
  const unrated = asArray(data.unrated);
  const unratedHours = Number(totals.unrated_hours || 0);
  // STEP 2 · A2: margin / fee status as the API sends it (billing_rate.margin_view, D-2 / D-3 — the dashboard's one rule)
  const marginStatus = totals.margin_status;
  const feeSet = totals.fee_set !== false;

  return (
    <div className="space-y-6" data-testid="billing-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Engagement Billing / Over Run Status</h1>
          <p className="text-sm text-muted-foreground">
            {engagement.engagement_name || engagement.client_name} · FY{engagement.financial_year}
            {engagement.firm_engagement_ref ? <> · <span className="font-mono">{engagement.firm_engagement_ref}</span></> : null}
            {currency ? <> · <span data-testid="billing-currency">{currency}</span></> : null}
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={openEdit} data-testid="billing-edit-fee-budget">
            <Pencil className="me-1 h-3 w-3" /> Edit fee / budget
          </Button>
          <Button size="sm" variant="outline" onClick={() => exportFile("xlsx")} data-testid="billing-export-xlsx">
            <Download className="me-1 h-3 w-3" /> XLSX
          </Button>
          <Button size="sm" variant="outline" onClick={() => exportFile("pdf")} data-testid="billing-export-pdf">
            <Download className="me-1 h-3 w-3" /> PDF
          </Button>
        </div>
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Fee / Budget</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Total Contracted Revenue / Fee</Label>
              <Input type="number" min="0" step="0.01" value={editFee}
                     onChange={(e) => setEditFee(e.target.value)}
                     data-testid="billing-edit-fee-input" />
            </div>
            <div>
              <Label>Total Budgeted Hours</Label>
              <Input type="number" min="0" step="0.5" value={editHours}
                     onChange={(e) => setEditHours(e.target.value)}
                     data-testid="billing-edit-hours-input" />
            </div>
            <p className="text-[11px] text-muted-foreground">
              Every change is logged to the Audit Trail (old → new, by whom, when).
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={savingEdit}
                    className="bg-slate-900 hover:bg-slate-800" data-testid="billing-edit-save">
              {savingEdit ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Top KPIs */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">Contracted Fee</CardTitle></CardHeader>
          <CardContent>{feeSet
            ? <div className="text-2xl font-semibold" data-testid="kpi-fee">{fmt(engagement.contracted_fee, currency)}</div>
            : <div className="text-sm text-muted-foreground" data-testid="kpi-fee" data-status="fee_not_set">{t("fd.billing.feeNotSet")}</div>}</CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">Total Cost</CardTitle></CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold" data-testid="kpi-cost">{fmt(totals.cost, currency)}</div>
            {(totals.other_cost || 0) > 0 && (
              <div className="mt-1 text-[11px] text-muted-foreground" data-testid="kpi-cost-breakdown">
                Staff <span className="font-medium tabular-nums text-slate-700 dark:text-slate-200">{fmt(totals.staff_cost, currency)}</span>
                {" · "}
                Other <span className="font-medium tabular-nums text-slate-700 dark:text-slate-200">{fmt(totals.other_cost, currency)}</span>
              </div>
            )}
          </CardContent>
        </Card>
        <Card className={marginStatus === "ok" && totals.margin < 0 ? "border-red-300" : ""}>
          <CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">Margin</CardTitle></CardHeader>
          <CardContent>
            {/* STEP 2 · A2: the dashboard's D-2 / D-3 rule (backend billing_rate.margin_view) — no figure while an hour has no rate */}
            {marginStatus === "incomplete" ? (
              <div className="text-sm font-semibold text-amber-700 dark:text-amber-300" data-testid="kpi-margin" data-status="incomplete">
                {fill(t("fd.billing.incomplete"), { h: fmtHours((totals.margin_incomplete && totals.margin_incomplete.unrated_hours) ?? totals.unrated_hours, lang, t) })}
              </div>
            ) : marginStatus === "fee_not_set" ? (
              <div className="text-sm text-muted-foreground" data-testid="kpi-margin" data-status="fee_not_set">{t("fd.billing.feeNotSet")}</div>
            ) : (
              <div className={`text-2xl font-semibold ${totals.margin < 0 ? "text-red-600" : "text-emerald-600"}`} data-testid="kpi-margin" data-status={marginStatus}>
                {totals.margin < 0 ? <TrendingDown className="me-1 inline h-5 w-5" /> : <TrendingUp className="me-1 inline h-5 w-5" />}
                {fmt(totals.margin, currency)}
              </div>
            )}
          </CardContent>
        </Card>
        <Card className={budgetSet && totals.is_overrun ? "border-amber-400 bg-amber-50 dark:bg-amber-950/30" : ""}>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
              {budgetSet && totals.is_overrun && <AlertTriangle className="h-3 w-3 text-amber-600" />}
              Budget vs Actual
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold" data-testid="kpi-hours">
              {totals.hours_spent}
              {budgetSet ? <span className="text-sm text-muted-foreground"> / {engagement.budgeted_hours} hrs</span>
                         : <span className="text-sm text-muted-foreground"> hrs</span>}
            </div>
            {!budgetSet && (
              <div className="mt-1 text-xs text-slate-500 dark:text-slate-400" data-testid="billing-no-budget">
                {t("dsBilling.noBudgetSet")}
              </div>
            )}
            {budgetSet && totals.is_overrun && (
              <div className="mt-1 text-xs font-medium text-amber-800 dark:text-amber-200" data-testid="overrun-badge">
                OVER RUN · +{totals.overrun_hours} hrs ({totals.overrun_pct}% over)
              </div>
            )}
            {budgetSet && !totals.is_overrun ? (
              <div className="mt-1 text-xs text-muted-foreground">{totals.budget_used_pct}% of budget used</div>
            ) : null}
          </CardContent>
        </Card>
      </div>

      {/* By User */}
      <Card>
        <CardHeader><CardTitle className="text-base">By User</CardTitle></CardHeader>
        <CardContent className="p-0">
          <ResizableTable cols={colW} wrap className="w-full text-sm">
            <thead className="border-b bg-slate-50 text-xs uppercase tracking-wider text-muted-foreground dark:bg-slate-900/60">
              <tr>
                <ResizableTh index={0} className="px-4 py-2 text-start">User</ResizableTh>
                <ResizableTh index={1} className="px-4 py-2 text-start">Billing Grade</ResizableTh>
                <ResizableTh index={2} className="px-4 py-2 text-end">{t("dsBilling.appliedRate")}</ResizableTh>
                <ResizableTh index={3} className="px-4 py-2 text-end">Actual (h)</ResizableTh>
                <ResizableTh index={4} className="px-4 py-2 text-end">Budgeted (h)</ResizableTh>
                <ResizableTh index={5} className="px-4 py-2 text-end">Used %</ResizableTh>
                <ResizableTh index={6} className="px-4 py-2 text-end">Cost</ResizableTh>
              </tr>
            </thead>
            <tbody>
              {by_user.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">No time logged yet on this engagement.</td></tr>
              )}
              {by_user.map((u) => {
                const pct = u.used_pct;
                const pctColor = pct == null ? "text-muted-foreground"
                                  : pct >= 100 ? "text-red-600 font-semibold"
                                  : pct >= 80  ? "text-amber-600 font-semibold"
                                              : "text-emerald-700";
                return (
                  <tr key={u.user_id} className={`border-b ${td.rowClass(u.user_id)}`} data-changed={td.isChanged(u.user_id) ? "1" : "0"} data-failed={td.errorOf(u.user_id) ? "1" : "0"} data-testid={`billing-user-${u.user_id}`}>
                    <td className="px-4 py-2 font-medium">{u.user_name}<RowSaveError draft={td} id={u.user_id} testid={`billing-user-error-${u.user_id}`} /></td>
                    <td className="px-4 py-2">
                      <GradePicker
                        grade={u.grade}
                        userId={u.user_id}
                        userName={u.user_name}
                        grades={grades}
                        onAssigned={refresh}
                      />
                    </td>
                    <td className={`px-4 py-2 num-cell ${isUnrated(u) ? "text-slate-500 dark:text-slate-400" : ""}`}
                        data-testid={`billing-user-rate-${u.user_id}`}>{rateText(u, t, currency)}</td>
                    <td className="px-4 py-2 num-cell">
                      <button type="button"
                                onClick={() => setBreakdownUser(
                                  { user_id: u.user_id, user_name: u.user_name })}
                                className="group inline-flex items-center gap-1 rounded px-1 hover:bg-emerald-50 dark:hover:bg-emerald-950/40"
                                title="See per-session breakdown"
                                data-testid={`billing-user-hours-${u.user_id}`}>
                        <span className="tabular-nums underline-offset-2 group-hover:underline">
                          {hrs(u.seconds)}
                        </span>
                        <Info className="h-3 w-3 text-slate-400 group-hover:text-emerald-600" />
                      </button>
                    </td>
                    <td className="px-4 py-2 num-cell">
                      <input type="number" step="0.5" min="0"
                              value={u.user_id ? (td.rowValue(u).budgeted_hours ?? "") : (u.budgeted_hours || "")}
                              placeholder="—"
                              disabled={!u.user_id}
                              onChange={(e) => td.edit(u.user_id, "budgeted_hours", e.target.value === "" ? "" : Number(e.target.value))}
                              className="w-20 rounded border px-2 py-1 text-end text-xs tabular-nums focus:border-emerald-500 focus:outline-none dark:bg-slate-900"
                              data-testid={`billing-user-budget-${u.user_id}`} />
                    </td>
                    <td className={`px-4 py-2 num-cell ${pctColor}`} data-testid={`billing-user-pct-${u.user_id}`}>
                      {pct == null ? "—" : `${pct}%`}
                      {u.is_overrun && <span className="ms-1 rounded bg-red-50 px-1.5 py-0.5 text-[9px] font-semibold text-red-700">OVER</span>}
                    </td>
                    <td className={`px-4 py-2 num-cell ${u.cost == null ? "text-slate-500 dark:text-slate-400" : "font-semibold"}`}
                        data-testid={`billing-user-cost-${u.user_id}`}>{costText(u, t, currency)}</td>
                  </tr>
                );
              })}
            </tbody>
          </ResizableTable>
          <TableSaveBar draft={td} lang={lang} testid="billing-budget-save" />
        </CardContent>
      </Card>

      {/* By Grade */}
      <Card>
        <CardHeader><CardTitle className="text-base">By Billing Grade</CardTitle></CardHeader>
        <CardContent className="p-0">
          <ResizableTable cols={colW2} wrap className="w-full text-sm">
            <thead className="border-b bg-slate-50 text-xs uppercase tracking-wider text-muted-foreground dark:bg-slate-900/60">
              <tr>
                <ResizableTh index={0} className="px-4 py-2 text-start">Grade</ResizableTh>
                <ResizableTh index={1} className="px-4 py-2 text-end">{t("dsBilling.appliedRate")}</ResizableTh>
                <ResizableTh index={2} className="px-4 py-2 text-end">Hours</ResizableTh>
                <ResizableTh index={3} className="px-4 py-2 text-end">Cost</ResizableTh>
              </tr>
            </thead>
            <tbody>
              {by_grade.map((g) => (
                <tr key={g.grade} className="border-b" data-testid={`billing-grade-${g.grade}`}>
                  <td className="px-4 py-2 font-medium"><GradeBadge grade={g.grade} testid={`billing-grade-badge-${g.grade}`} /></td>
                  <td className={`px-4 py-2 num-cell ${isUnrated(g) ? "text-slate-500 dark:text-slate-400" : ""}`}>{rateText(g, t, currency)}</td>
                  <td className="px-4 py-2 num-cell">{hrs(g.seconds)}</td>
                  <td className={`px-4 py-2 num-cell ${g.cost == null ? "text-slate-500 dark:text-slate-400" : "font-semibold"}`}>{costText(g, t, currency)}</td>
                </tr>
              ))}
            </tbody>
          </ResizableTable>
        </CardContent>
      </Card>

      {/* Stage 2 Dashboard · 2.5 / A-18: hours with no rate — listed apart, never costed at 0 */}
      <Card data-testid="billing-unrated" className={unrated.length ? "border-slate-300" : ""}>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex flex-wrap items-center gap-2">
            {t("dsBilling.rateNotSet")}
            <span className="text-xs font-normal text-slate-500 dark:text-slate-400">
              {t("dsBilling.hoursNoRate")}: <span className="tabular-nums" data-testid="billing-unrated-hours">{unratedHours.toFixed(2)}</span>
            </span>
          </CardTitle>
          <p className="text-[11px] text-muted-foreground">{t("dsBilling.hoursNoRateHint")}</p>
        </CardHeader>
        <CardContent className="p-0">
          {unrated.length === 0 ? (
            <div className="px-4 py-4 text-sm text-muted-foreground">{t("dsBilling.noneUnrated")}</div>
          ) : (
            <ResizableTable cols={colW3} wrap className="w-full text-sm">
              <thead className="border-b bg-slate-50 text-xs uppercase tracking-wider text-muted-foreground dark:bg-slate-900/60">
                <tr>
                  <ResizableTh index={0} className="px-4 py-2 text-start">User</ResizableTh>
                  <ResizableTh index={1} className="px-4 py-2 text-start">Billing Grade</ResizableTh>
                  <ResizableTh index={2} className="px-4 py-2 text-end">Hours</ResizableTh>
                  <ResizableTh index={3} className="px-4 py-2 text-start">{t("dsBilling.why")}</ResizableTh>
                </tr>
              </thead>
              <tbody>
                {unrated.map((u) => (
                  <tr key={u.user_id} className="border-b" data-testid={`billing-unrated-${u.user_id}`}>
                    <td className="px-4 py-2 font-medium">{u.user_name}</td>
                    <td className="px-4 py-2"><GradeBadge grade={u.grade} /></td>
                    <td className="px-4 py-2 num-cell">{hrs(u.seconds)}</td>
                    <td className="px-4 py-2 text-slate-500 dark:text-slate-400">{t(`dsBilling.reason.${u.reason || "no_rate_on_entry"}`)}</td>
                  </tr>
                ))}
              </tbody>
            </ResizableTable>
          )}
        </CardContent>
      </Card>

      {/* Other Admin Cost — itemized non-hourly costs. Partner-only (tab is
          already gated, so no extra guard here). Every mutation writes an
          audit-trail row identical to fee/budget edits. */}
      <OtherAdminCostSection
        engagementId={current.id}
        currency={currency}
        rows={data.other_costs || []}
        total={totals.other_cost || 0}
        onChanged={refresh}
      />

      <p className="text-[11px] text-muted-foreground" data-testid="billing-rate-note">
        <Clock className="me-1 inline h-3 w-3" />
        {t("dsBilling.rateNote")}
      </p>

      {/* Per-user hours breakdown modal — click any Actual (h) cell */}
      <HoursBreakdownDialog
        engagementId={current?.id}
        target={breakdownUser}
        onClose={() => setBreakdownUser(null)}
        currency={currency}
      />
    </div>
  );
}


// ==============================================================
// Other Admin Cost — itemized non-hourly engagement costs. Partner-only
// (the whole Billing tab is already Partner-gated, but every mutation
// hits a Partner-only server endpoint too). Every add/edit/delete
// writes an audit-trail row via the backend's log_event_fn hook.
// ==============================================================
function OtherAdminCostSection({ engagementId, currency, rows, total, onChanged }) {
  const colW4 = useResizableColumns({ storageKey: "billing-other-admin-costs", defaults: [130, 160, 240, 130, 130], mins: [96, 100, 130, 90, 90] });   // Step 11d · add-on
  const [dialog, setDialog] = useState(null); // null | {mode:'create'|'edit', row?:{...}}
  const openCreate = () => setDialog({ mode: "create", row: {
    amount: "", description: "", category: "Other", cost_date: todayISO(),
  }});
  const openEdit = (r) => setDialog({ mode: "edit", row: {
    id: r.id, amount: r.amount, description: r.description || "",
    category: r.category || "Other", cost_date: r.cost_date,
  }});
  const close = () => setDialog(null);

  const remove = async (r) => {
    if (!await askConfirm(`Delete "${r.description}" (${currency} ${fmtNumber(Number(r.amount))})?`)) return;
    try {
      await api.delete(`/billing/${engagementId}/other-costs/${r.id}`);
      toast.success("Removed — logged to Audit Trail");
      onChanged && onChanged();
    } catch (e) { toast.error(toErrMsg(e, "Delete failed")); }
  };

  return (
    <Card data-testid="other-admin-cost-section">
      <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
        <div>
          <CardTitle className="text-base">Other Admin Cost</CardTitle>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            Itemized non-hourly costs (travel, subscriptions, disbursements). Adds to Total Cost.
          </p>
        </div>
        <Button size="sm" onClick={openCreate}
                 className="bg-slate-900 hover:bg-slate-800"
                 data-testid="other-cost-add-btn">
          <Plus className="me-1 h-3 w-3" /> Add cost
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        <ResizableTable cols={colW4} wrap className="w-full text-sm">
          <thead className="border-b bg-slate-50 text-xs uppercase tracking-wider text-muted-foreground dark:bg-slate-900/60">
            <tr>
              <ResizableTh index={0} className="px-4 py-2 text-start">Date</ResizableTh>
              <ResizableTh index={1} className="px-4 py-2 text-start">Category</ResizableTh>
              <ResizableTh index={2} className="px-4 py-2 text-start">Description</ResizableTh>
              <ResizableTh index={3} className="px-4 py-2 text-end">Amount</ResizableTh>
              <ResizableTh index={4} className="px-4 py-2 text-end w-24">Actions</ResizableTh>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground"
                    data-testid="other-cost-empty">
                  No itemized costs yet. Click <b>Add cost</b> to record travel, subscriptions or disbursements.
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="border-b" data-testid={`other-cost-row-${r.id}`}>
                <td className="px-4 py-2 whitespace-nowrap tabular-nums">{fmtDateDt(r.cost_date)}</td>
                <td className="px-4 py-2">
                  <span className="rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                    {r.category || "Other"}
                  </span>
                </td>
                <td className="px-4 py-2 max-w-[380px] break-words text-slate-700 dark:text-slate-200"
                    title={r.description}>{r.description}</td>
                <td className="px-4 py-2 num-cell font-semibold">{fmt(r.amount, currency)}</td>
                <td className="px-4 py-2 text-end">
                  <div className="inline-flex items-center gap-1">
                    <button type="button" onClick={() => openEdit(r)}
                            className="rounded p-1 hover:bg-slate-100 dark:hover:bg-slate-800"
                            title="Edit"
                            data-testid={`other-cost-edit-${r.id}`}>
                      <Pencil className="h-3.5 w-3.5 text-slate-500" />
                    </button>
                    <button type="button" onClick={() => remove(r)}
                            className="rounded p-1 hover:bg-red-50 dark:hover:bg-red-950/40"
                            title="Delete"
                            data-testid={`other-cost-delete-${r.id}`}>
                      <Trash2 className="h-3.5 w-3.5 text-red-500" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr className="border-t bg-slate-50 font-semibold dark:bg-slate-900/60">
                <td colSpan={3} className="px-4 py-2">Total Other Admin Cost</td>
                <td className="px-4 py-2 num-cell" data-testid="other-cost-total">
                  {fmt(total, currency)}
                </td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </ResizableTable>
      </CardContent>
      <OtherAdminCostDialog
        engagementId={engagementId}
        currency={currency}
        dialog={dialog}
        onClose={close}
        onSaved={() => { close(); onChanged && onChanged(); }}
      />
    </Card>
  );
}


function OtherAdminCostDialog({ engagementId, currency, dialog, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(dialog?.row || {
    amount: "", description: "", category: "Other", cost_date: todayISO(),
  });
  const seedFromRow = useEffectEvent(() => {
    if (dialog?.row) setForm({
      ...dialog.row,
      amount: dialog.row.amount ?? "",
    });
  });
  useEffect(() => { seedFromRow(); }, [dialog?.mode, dialog?.row?.id, seedFromRow]);

  const isEdit = dialog?.mode === "edit";
  const submit = async () => {
    const amt = Number(form.amount);
    if (!amt || amt <= 0) { toast.error("Amount must be greater than 0"); return; }
    if (!(form.description || "").trim()) { toast.error("Description is required"); return; }
    if (!form.cost_date) { toast.error("Date is required"); return; }
    setSaving(true);
    try {
      const body = {
        amount: amt,
        description: form.description.trim(),
        cost_date: form.cost_date,
        category: form.category || "Other",
      };
      if (isEdit) {
        await api.patch(`/billing/${engagementId}/other-costs/${form.id}`, body);
        toast.success("Updated — logged to Audit Trail");
      } else {
        await api.post(`/billing/${engagementId}/other-costs`, body);
        toast.success("Added — logged to Audit Trail");
      }
      onSaved();
    } catch (e) {
      toast.error(toErrMsg(e, "Save failed"));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!dialog} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="other-cost-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Other Admin Cost" : "Add Other Admin Cost"}</DialogTitle>
          <DialogDescription>
            Itemized non-hourly cost recorded against this engagement. Every change is logged to the Audit Trail.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-1">
            <Label>Date</Label>
            <DateInput value={form.cost_date || ""}
                    onChange={(v) => setForm({ ...form, cost_date: v })}
                    data-testid="other-cost-date" />
          </div>
          <div className="col-span-1">
            <Label>Category</Label>
            <Select value={form.category || "Other"}
                     onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="other-cost-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {OTHER_COST_CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c} data-testid={`other-cost-cat-${c}`}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-1">
            <Label>Amount ({currency})</Label>
            <Input type="number" min="0" step="0.01" value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    data-testid="other-cost-amount" />
          </div>
          <div className="col-span-2">
            <Label>Description</Label>
            <Input value={form.description || ""}
                    placeholder="e.g. Flight DXB→RUH, ISQM subscription renewal"
                    maxLength={500}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    data-testid="other-cost-desc" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving}
                   className="bg-slate-900 hover:bg-slate-800"
                   data-testid="other-cost-save">
            {saving ? "Saving…" : (isEdit ? "Save changes" : "Add cost")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
function GradePicker({ grade, userId, userName, grades, onAssigned }) {
  const { user } = useAuth();
  const isPartner = user?.role === "Partner" || user?.role === "Admin";
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const isUnassigned = !grade || grade === "(no grade)";

  const save = async (newGrade) => {
    // Defence-in-depth: verify userId at CALL time (not just prop time) so a
    // stale closure or a mid-render prop reset can't slip an undefined id
    // through to the backend and trigger a Pydantic 422 "Field required".
    if (!userId || typeof userId !== "string" || !userId.trim()) {
      toast.error("Cannot assign grade — user id missing. Please refresh the page.");
      return;
    }
    setSaving(true);
    try {
      await api.patch("/billing/user-grade",
        { user_id: userId, grade: newGrade || "" },
        { _silent: true });
      toast.success(newGrade
        ? `${userName} → ${newGrade}`
        : `${userName} → (no grade)`);
      setOpen(false);
      onAssigned && onAssigned();
    } catch (e) {
      toast.error(toErrMsg(e, "Failed to assign grade"));
    } finally { setSaving(false); }
  };

  if (!isPartner) {
    return <GradeBadge grade={grade}
                        testid={`billing-user-grade-${userId}`} />;
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="cursor-pointer"
                  data-testid={`billing-user-grade-${userId}`}
                  title={isUnassigned ? "Click to assign a grade" : "Click to change grade"}>
          <GradeBadge grade={grade} />
          {isUnassigned && (
            <span className="ms-1 text-[9px] font-medium uppercase tracking-wider text-emerald-600 hover:underline"
                    data-testid={`billing-user-set-grade-${userId}`}>
              Set grade
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2" data-testid={`billing-user-grade-picker-${userId}`}>
        <div className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">
          Assign billing grade
        </div>
        {grades.length === 0 && (
          <div className="p-2 text-xs text-muted-foreground">
            No grades in the Rate Card. Add one in <b>Settings → Billing Grade Rate Card</b>.
          </div>
        )}
        <div className="max-h-56 overflow-y-auto">
          {grades.map((g) => (
            <button key={g} type="button" onClick={() => save(g)}
                     disabled={saving || g === grade}
                     className={`flex w-full items-center gap-2 rounded px-2 py-1 text-start text-xs hover:bg-slate-100 disabled:opacity-50 dark:hover:bg-slate-800 ${
                       g === grade ? "bg-emerald-50 font-medium dark:bg-emerald-950/40" : ""
                     }`}
                     data-testid={`billing-user-grade-option-${userId}-${g}`}>
              <GradeBadge grade={g} />
              {g === grade && <span className="ms-auto text-emerald-600">•</span>}
            </button>
          ))}
        </div>
        {!isUnassigned && (
          <button type="button" onClick={() => save("")}
                    disabled={saving}
                    className="mt-2 w-full rounded border border-red-200 px-2 py-1 text-[11px] text-red-600 hover:bg-red-50 disabled:opacity-50 dark:border-red-900 dark:hover:bg-red-950/40"
                    data-testid={`billing-user-grade-clear-${userId}`}>
            Clear grade
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}


// ==============================================================
// Per-user hours breakdown. Powers the click-through from the "Actual (h)"
// cell. Fetches /billing/{eng}/user/{uid}/entries — Partner sees rate + cost;
// self-view hides both (server strips them).
// ==============================================================
function HoursBreakdownDialog({ engagementId, target, onClose, currency }) {
  const colW5 = useResizableColumns({ storageKey: "billing-hours-breakdown", defaults: [160, 160, 130, 130, 110, 240, 130, 130], mins: [100, 100, 90, 90, 72, 130, 90, 90] });   // Step 11d · add-on
  const { t } = useI18n();   // Stage 2 Dashboard · 2.5: per-entry applied rate, "Rate not set"
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const hasTarget = !!target, targetUserId = target?.user_id;   // Step 7c · E3: the two parts of target it reads
  useEffect(() => {
    if (!hasTarget || !engagementId) { setPayload(null); return; }
    setLoading(true);
    api.get(`/billing/${engagementId}/user/${targetUserId}/entries`)
      .then((r) => setPayload(r.data))
      .catch(() => setPayload(null))
      .finally(() => setLoading(false));
  }, [engagementId, hasTarget, targetUserId]);

  const hrsMin = (secs) => {
    const s = Math.max(0, Number(secs || 0));
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
    return h ? `${h}h ${m}m` : `${m}m`;
  };

  return (
    <Dialog open={!!target} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl" data-testid="billing-hours-breakdown">
        <DialogHeader>
          <DialogTitle>
            Hours breakdown — {target?.user_name || ""}
          </DialogTitle>
          <DialogDescription>{t("dsBilling.breakdownHint")}</DialogDescription>
        </DialogHeader>
        {loading && <div className="p-6 text-sm text-muted-foreground">Loading…</div>}
        {!loading && payload && (
          <>
            <div className="grid grid-cols-2 gap-3 rounded-md bg-slate-50 p-3 text-xs dark:bg-slate-900/50 sm:grid-cols-4">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Entries</div>
                <div className="text-sm font-semibold tabular-nums">{payload.entry_count}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total hours</div>
                <div className="text-sm font-semibold tabular-nums" data-testid="breakdown-total-hours">
                  {(payload.total_seconds / 3600).toFixed(2)}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Break excluded</div>
                <div className="text-sm font-semibold tabular-nums text-amber-700">
                  {hrsMin(payload.total_break_seconds)}
                </div>
              </div>
              {"total_cost" in payload && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    {t("dsBilling.breakdownCost")}{payload.current_grade ? ` · ${payload.current_grade}` : ""}
                  </div>
                  <div className="text-sm font-semibold tabular-nums" data-testid="breakdown-total-cost">
                    {fmt(payload.total_cost, currency)}
                  </div>
                  {Number(payload.unrated_seconds || 0) > 0 && (
                    <div className="text-[10px] text-slate-500 dark:text-slate-400" data-testid="breakdown-unrated">
                      {t("dsBilling.breakdownNoRate")}: {hrs(payload.unrated_seconds)}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="max-h-[420px] overflow-y-auto rounded-md border">
              <ResizableTable cols={colW5} className="w-full text-xs">
                <thead className="border-b bg-slate-50 uppercase tracking-wider text-muted-foreground dark:bg-slate-900/60">
                  <tr>
                    <ResizableTh index={0} className="px-3 py-2 text-start">Started</ResizableTh>
                    <ResizableTh index={1} className="px-3 py-2 text-start">Ended</ResizableTh>
                    <ResizableTh index={2} className="px-3 py-2 text-end">Duration</ResizableTh>
                    <ResizableTh index={3} className="px-3 py-2 text-end">Break</ResizableTh>
                    <ResizableTh index={4} className="px-3 py-2 text-start">Source</ResizableTh>
                    <ResizableTh index={5} className="px-3 py-2 text-start">Note</ResizableTh>
                    {"total_cost" in payload && <ResizableTh index={6} className="px-3 py-2 text-end">{t("dsBilling.appliedRate")}</ResizableTh>}
                    {"total_cost" in payload && <ResizableTh index={7} className="px-3 py-2 text-end">{t("dsBilling.breakdownCost")}</ResizableTh>}
                  </tr>
                </thead>
                <tbody>
                  {payload.entries.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-3 py-6 text-center text-muted-foreground">
                        No entries yet.
                      </td>
                    </tr>
                  )}
                  {payload.entries.map((e) => (
                    <tr key={e.id} className="border-b"
                          data-testid={`breakdown-entry-${e.id}`}>
                      <td className="px-3 py-2 whitespace-nowrap">{fmtDateDt(e.started_at)}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{fmtDateDt(e.ended_at)}</td>
                      <td className="px-3 py-2 num-cell">{hrsMin(e.seconds)}</td>
                      <td className={`px-3 py-2 num-cell ${e.on_break_seconds > 0 ? "text-amber-700" : "text-muted-foreground"}`}>
                        {e.on_break_seconds > 0 ? hrsMin(e.on_break_seconds) : "—"}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`rounded-md px-1.5 py-0.5 text-[10px] font-medium ${
                          e.source === "manual"
                            ? "bg-amber-100 text-amber-800"
                            : "bg-emerald-100 text-emerald-800"
                        }`}>
                          {e.source === "manual" ? "manual" : "auto (session)"}
                        </span>
                      </td>
                      <td className="px-3 py-2 max-w-[240px] break-words text-slate-600 dark:text-slate-300">
                        {e.note || "—"}
                      </td>
                      {"total_cost" in payload && (
                        <td className={`px-3 py-2 num-cell ${e.rate == null ? "text-slate-500 dark:text-slate-400" : ""}`}
                            data-testid={`breakdown-entry-rate-${e.id}`}>
                          {entryRateText(e, t, (v) => fmt(v, e.currency || currency))}
                        </td>
                      )}
                      {"total_cost" in payload && (
                        <td className={`px-3 py-2 num-cell ${e.cost == null ? "text-slate-500 dark:text-slate-400" : ""}`}>
                          {e.cost == null ? t("dsBilling.rateNotSet") : fmt(e.cost, e.currency || currency)}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t bg-slate-50 font-semibold dark:bg-slate-900/60">
                    <td colSpan={2} className="px-3 py-2">Total</td>
                    <td className="px-3 py-2 num-cell" data-testid="breakdown-sum-check">
                      {hrsMin(payload.total_seconds)}
                    </td>
                    <td className="px-3 py-2 num-cell text-amber-700">
                      {hrsMin(payload.total_break_seconds)}
                    </td>
                    <td colSpan={"total_cost" in payload ? 3 : 2}></td>
                    {"total_cost" in payload && (
                      <td className="px-3 py-2 num-cell">
                        {fmt(payload.total_cost, currency)}
                      </td>
                    )}
                  </tr>
                </tfoot>
              </ResizableTable>
            </div>
          </>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
